import clsx from 'clsx';
import React, { HTMLAttributes, ReactNode, useEffect } from 'react';

import { Portal } from '~/components/portal';

import { Button } from '../button/Button';
import styles from './Drawer.module.scss';

export const DRAWER_WIDTH = 600;

export enum CloseTrigger {
  CloseButtonClick = 'CloseButtonClick',
  BackgroundClick = 'BackgroundClick',
  EscapeKey = 'EscapeKey',
}

export interface DrawerProps extends HTMLAttributes<HTMLElement> {
  headerButton?: ReactNode;
  isOpen: boolean;
  handleCloseClick: () => void;
  customBackgroundClickHandler?: () => void;
  closeTriggers?: CloseTrigger | CloseTrigger[];
  closeIcon?: boolean;
  className?: string;
  children: ReactNode;
  customHeader?: ReactNode;
}

export const Drawer = ({
  className,
  headerButton,
  handleCloseClick,
  customBackgroundClickHandler,
  closeTriggers = [
    CloseTrigger.CloseButtonClick,
    CloseTrigger.BackgroundClick,
    CloseTrigger.EscapeKey,
  ],
  isOpen,
  children,
  closeIcon = true,
  customHeader,
  ...rest
}: DrawerProps) => {
  const closeTriggersArray = Array.isArray(closeTriggers)
    ? closeTriggers
    : [closeTriggers];

  useEffect(() => {
    const keyHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleCloseClick();
      }
    };

    if (closeTriggersArray.includes(CloseTrigger.EscapeKey) && isOpen) {
      window.addEventListener('keyup', keyHandler);
    }

    return () => {
      window.removeEventListener('keyup', keyHandler);
    };
  }, [isOpen, handleCloseClick]);

  const handleBackgroundOverlayClick = () => {
    if (closeTriggersArray.includes(CloseTrigger.BackgroundClick)) {
      customBackgroundClickHandler
        ? customBackgroundClickHandler()
        : handleCloseClick();
    }
  };

  const handleCloseButtonClick = () => {
    if (closeTriggersArray.includes(CloseTrigger.CloseButtonClick)) {
      handleCloseClick();
    }
  };

  return (
    <Portal>
      <div className={className} aria-hidden={!isOpen} inert={!isOpen}>
        <div
          onClick={handleBackgroundOverlayClick}
          className={clsx(styles.backgroundOverlay, {
            [styles.backgroundOverlayOpen]: isOpen,
          })}
          data-testid="background-overlay"
        />
        <section
          data-testid="drawer-container"
          data-drawer-container
          className={clsx(styles.container, {
            [styles.containerOpen]: isOpen,
          })}
          {...rest}
        >
          {customHeader ?? (
            <div className={styles.header} data-drawer-header>
              {headerButton ?? <div />}
              {closeIcon && (
                <Button
                  className={styles.closeButton}
                  data-drawer-header-close-btn
                  data-testid="drawer-header-close-btn"
                  onClick={handleCloseButtonClick}
                  iconName="Close"
                  variant="empty"
                />
              )}
            </div>
          )}
          <div className={styles.body} data-drawer-body>
            {children}
          </div>
        </section>
      </div>
    </Portal>
  );
};
